import { BusinessPlanSection, GenerationContext, SectionId, SECTION_METADATA, SectionStatus } from '../types/businessPlan';
import { ENV } from '../config/env';
import { supabase } from '../lib/supabase';

interface RequestOptions {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: any;
}

export class AnthropicService {
  // Get the authentication token from Supabase
  private async getToken(): Promise<string> {
    try {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token || '';
    } catch (e) {
      console.error('Error getting Supabase auth token:', e);
      return '';
    }
  }

  private async makeRequest<T>(options: RequestOptions): Promise<T> {
    const { endpoint, method = 'POST', data } = options;
    const url = `${ENV.API_URL}${endpoint}`;

    try {
      console.log(`Making ${method} request to ${url}`);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getToken()}`
        },
        body: data ? JSON.stringify(data) : undefined,
      });

      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response as JSON:', responseText);
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${responseText}`);
      }
      
      if (!response.ok) {
        console.error('API request failed:', responseData);
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${JSON.stringify(responseData)}`);
      }

      return responseData;
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  async generateCompletePlan(context: GenerationContext): Promise<Record<SectionId, BusinessPlanSection>> {
    // Ensure the businessIdea has all required properties
    if (!context.businessIdea) {
      throw new Error('Business idea is required for plan generation');
    }

    console.log('Generating business plan sections individually...');
    
    // Get the ordered list of sections to generate
    const sectionIds = Object.keys(SECTION_METADATA) as SectionId[];
    
    // Initialize result with empty sections to satisfy TypeScript
    const result: Record<SectionId, BusinessPlanSection> = {} as Record<SectionId, BusinessPlanSection>;
    
    // Generate each section individually
    for (const sectionId of sectionIds) {
      try {
        console.log(`Generating section: ${sectionId}`);
        const section = await this.generateSingleSection(sectionId, context);
        result[sectionId] = section;
      } catch (error) {
        console.error(`Error generating section ${sectionId}:`, error);
        // Create a placeholder for the failed section
        result[sectionId] = {
          id: sectionId,
          title: SECTION_METADATA[sectionId].title,
          content: `Error generating content for ${SECTION_METADATA[sectionId].title}. Please regenerate this section.`,
          status: 'error',
          pageNumber: SECTION_METADATA[sectionId].pageNumber || 0
        };
      }
    }
    
    return result;
  }
  
  private async generateSingleSection(sectionId: SectionId, context: GenerationContext): Promise<BusinessPlanSection> {
    // Format the request data to match backend expectations
    const requestData = {
      sectionId,
      context: {
        businessIdea: {
          title: context.businessIdea.title,
          description: context.businessIdea.description,
          potentialMarket: context.businessIdea.potentialMarket,
          skillsUtilized: context.businessIdea.skillsUtilized,
          id: context.businessIdea.id,
          createdAt: context.businessIdea.createdAt
        }
      }
    };

    // Make the API request
    const section = await this.makeRequest<BusinessPlanSection>({
      endpoint: '/api/business-plans/generate-section',
      data: requestData,
    });
    
    // Ensure the section has a pageNumber
    if (!section.pageNumber) {
      section.pageNumber = SECTION_METADATA[sectionId].pageNumber || 0;
    }
    
    return section;
  }

  async regenerateSection(sectionId: SectionId, context: GenerationContext): Promise<BusinessPlanSection> {
    try {
      const regenerated = await this.makeRequest<BusinessPlanSection>({
        endpoint: '/api/business-plans/regenerate-section',
        data: { sectionId, context }
      });

      if (!regenerated?.pageNumber) {
        regenerated.pageNumber = SECTION_METADATA[sectionId].pageNumber;
      }
      return regenerated;
    } catch (error) {
      console.error('Error regenerating section:', error);
      return {
        id: sectionId,
        title: SECTION_METADATA[sectionId].title,
        content: '',
        status: 'error' as SectionStatus,
        pageNumber: SECTION_METADATA[sectionId].pageNumber,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async validateSection(section: BusinessPlanSection): Promise<{ isValid: boolean; feedback?: string }> {
    // Format the request data to match backend expectations
    const requestData = {
      section
    };

    console.log(`Validating section ${section.id}:`, JSON.stringify(requestData, null, 2));

    // Make the API request
    return this.makeRequest<{ isValid: boolean; feedback?: string }>({  
      endpoint: '/api/business-plans/validate-section',
      data: requestData,
    });
  }

  async generateText(prompt: string): Promise<string> {
    try {
      // Validate prompt
      if (!prompt) {
        throw new Error('Prompt is required');
      }

      console.log('Generating text with Anthropic...');
      
      // Get current user ID from Supabase
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }
      
      // Format the request data including userId
      const requestData = {
        prompt,
        max_tokens: 1000,
        temperature: 0.7,
        userId, // Include userId for auth middleware
      };

      // Make the API request
      const response = await this.makeRequest<{ text: string }>({
        endpoint: '/api/anthropic/generate',
        method: 'POST',
        data: requestData,
      });

      return response.text;
    } catch (error) {
      console.error('Error generating text:', error);
      throw error;
    }
  }

  // Generate subsections for a section by parsing markdown content.
  async generateSubsections(sectionId: SectionId, context: GenerationContext): Promise<BusinessPlanSection['subsections']> {
    try {
      // Prefer existing content in context
      const existing = context.sections?.[sectionId]?.content || '';
      let content = existing;
      if (!content) {
        // Fallback: regenerate the section to obtain content
        const regenerated = await this.regenerateSection(sectionId, context);
        content = regenerated.content || '';
      }

      return this.extractSubsectionsFromContent(content, sectionId);
    } catch (error) {
      console.error(`Error generating subsections for ${sectionId}:`, error);
      return [];
    }
  }

  // Simple parser to derive subsections from markdown/plain text
  private extractSubsectionsFromContent(content: string, sectionId: SectionId): BusinessPlanSection['subsections'] {
    if (!content) return [];

    const lines = content.split('\n');
    const subsections: BusinessPlanSection['subsections'] = [];
    let currentTitle = '';
    let currentContent: string[] = [];
    let idx = 0;

    const headerPatterns = [
      /^#+\s+(.+)/i,        // Markdown headers
      /^\d+\.\s+(.+)/i,   // Numbered headers
      /^([A-Z][^:]{2,}):\s*$/, // Title with colon
      /^[A-Z][\w\s&-]+$/   // All-caps or title-case line
    ];

    const pushCurrent = () => {
      if (currentTitle && currentContent.length) {
        subsections.push({
          id: `${sectionId}-subsection-${idx}`,
          title: currentTitle,
          content: currentContent.join('\n'),
          status: 'completed',
          pageNumber: SECTION_METADATA[sectionId].pageNumber + idx
        });
        idx++;
      }
    };

    for (const raw of lines) {
      const line = raw.trim();
      if (!line) continue;
      const isHeader = headerPatterns.some((re) => re.test(line));
      if (isHeader) {
        pushCurrent();
        currentTitle = line.replace(/^#+\s+/, '');
        currentContent = [];
      } else {
        if (!currentTitle) currentTitle = `${SECTION_METADATA[sectionId].title} Overview`;
        currentContent.push(line);
      }
    }
    pushCurrent();

    if (subsections.length === 0 && content.trim()) {
      subsections.push({
        id: `${sectionId}-subsection-0`,
        title: `${SECTION_METADATA[sectionId].title} Overview`,
        content,
        status: 'completed',
        pageNumber: SECTION_METADATA[sectionId].pageNumber
      });
    }

    return subsections;
  }
}

// Create a singleton instance of the AnthropicService
export const anthropicService = new AnthropicService();
